import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { apiGetById } from "../../services/genesisApi/httpsMethods";
import type { Deal } from "../../types/deal";
import type { DealImage } from "../../types/dealImages";
import DealImageCarousel from "./components/DealImageCarousel";
import { InfoSection } from "../../components/common/infoSection";
import "../../assets/css/Text-General.css";
import { dateTimeFormatter } from "../../utils/numberFormatter";
import { FormattedText } from "../../components/common/FormattedText";
import type { Sponsor } from "../../types/deal";
import { NonCircumventBadge } from "../../components/deal/NonCircumventBadge";
import { dealHasNda } from "../../types/nda";

interface DealDetailProps {
  dealId?: number;
  /** Live deal from parent — keeps NDA badge in sync after NDA tab saves. */
  deal?: Deal | null;
  hero?: DealImage | null;
  images?: DealImage[];
  onOpenGallery?: () => void;
  onOpenNda?: () => void;
  onHeroImageError?: () => void;
}

/** e.g. "Tom Jones" -> "TJ"; one word -> first letter. */
const nameToInitials = (name: string | null | undefined): string => {
  if (name == null) return "-";
  const t = name.trim();
  if (t === "" || t === "-") return "-";
  const parts = t.split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 0) return "-";
  if (parts.length === 1) {
    const c = parts[0]![0];
    return c ? c.toUpperCase() : "-";
  }
  const a = parts[0]![0] ?? "";
  const b = parts[parts.length - 1]![0] ?? "";
  return `${a}${b}`.toUpperCase();
};

const DealDetail = ({
  dealId,
  deal: dealFromParent,
  hero,
  images = [],
  onOpenGallery,
  onOpenNda,
  onHeroImageError,
}: DealDetailProps) => {
  const [deal, setDeal] = useState<Deal | null>(dealFromParent ?? null);
  const [loading, setLoading] = useState(!dealFromParent);

  const unpackYearBasedValue = (value: Record<string, number> | null) => {
    if (!value) return null;
    return Object.values(value).map(String).join(", ");
  };

  const formatBorrowers = (borrowers: any[] | undefined) => {
    if (!borrowers || borrowers.length === 0) return null;
    return borrowers
      .map((b) => {
        const name = b?.name ?? "";

        return `${name}`.trim();
      })
      .filter(Boolean)
      .join(" | ");
  };

  useEffect(() => {
    if (dealFromParent) {
      setDeal(dealFromParent);
      setLoading(false);
    }
  }, [dealFromParent]);

  useEffect(() => {
    if (!dealId) return;
    if (dealFromParent) return;

    setLoading(true);
    apiGetById("deal", dealId)
      .then((response) => {
        if (response && response.success && response.data) {
          setDeal(response.data[0]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching deal", err);
        setLoading(false);
      });
  }, [dealId, dealFromParent]);

  if (loading) return <CircularProgress />;
  if (!deal) return <Typography>No deal found</Typography>;

  const imageCarousel =
    hero || images.length > 0 ? (
      <DealImageCarousel
        hero={hero}
        images={images}
        onImageError={onHeroImageError}
        onClick={onOpenGallery}
      />
    ) : null;

  const sponsor =
    typeof deal.sponsor === "object" ? (deal.sponsor as Sponsor) : null;
  const secondarySponsor =
    typeof deal.secondary_sponsor === "object"
      ? (deal.secondary_sponsor as Sponsor)
      : null;

  const leadInit = nameToInitials(deal.deal_lead?.name);
  const supportInit = nameToInitials(deal.deal_support?.name);
  const updatedByInit = nameToInitials(
    deal.updated_by ? String(deal.updated_by) : null,
  );
  const createdByInit = nameToInitials(
    deal.created_by ? String(deal.created_by) : null,
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: "10px",
        flexDirection: "column",
        marginBottom: 2,
      }}
    >
      <Box
        sx={{
          display: "grid",
          width: "100%",
          rowGap: 1,
          columnGap: 2,
          // Equal thirds: name left, deal team in true centre, dates right
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
          alignItems: "center",
        }}
      >
        <Typography
          className="text-general-page-header"
          sx={{
            "&&": { height: "auto !important" },
            minWidth: 0,
            paddingBottom: 0,
            mb: 0,
            fontWeight: "500 !important",
            lineHeight: 1.3,
            justifySelf: { xs: "start", sm: "start" },
            textAlign: "left",
          }}
        >
          {deal.deal_name}
          {dealHasNda(deal) && (
            <NonCircumventBadge
              nda={deal.nda}
              ndaId={deal.nda_id}
              onOpenNda={onOpenNda}
            />
          )}
        </Typography>
        <Typography
          className="text-general-page-header"
          sx={{
            "&&": { height: "auto !important" },
            minWidth: 0,
            lineHeight: 1.3,
            mb: 0,
            // Second column: content centred in the middle track
            justifySelf: { xs: "center", sm: "center" },
            textAlign: "center",
            fontWeight: "500 !important",
          }}
        >
          <span style={{ fontWeight: 500 }}>Deal team:</span>
          {` ${leadInit} & ${supportInit}`}
        </Typography>
        {deal.updated_by ? (
          <Typography
            className="text-general-page-header"
            sx={{
              "&&": { height: "auto !important" },
              minWidth: 0,
              lineHeight: 1.3,
              mb: 0,
              justifySelf: { xs: "end", sm: "end" },
              textAlign: { xs: "right", sm: "right" },
              fontWeight: "500 !important",
            }}
          >
            {`Updated at ${dateTimeFormatter({ value: deal.updated_date })} by ${updatedByInit}`}
          </Typography>
        ) : (
          <Typography
            className="text-general-page-header"
            sx={{
              "&&": { height: "auto !important" },
              minWidth: 0,
              lineHeight: 1.3,
              mb: 0,
              justifySelf: { xs: "end", sm: "end" },
              textAlign: { xs: "right", sm: "right" },
              fontWeight: "500 !important",
            }}
          >
            {`Created at ${dateTimeFormatter({ value: deal.created_date })}${
              deal.created_by ? ` by ${createdByInit}` : ""
            }`}
          </Typography>
        )}
      </Box>
      {imageCarousel}
      <Box
        sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%" }}
      >
        <InfoSection
          title="Overview"
          items={[
            { label: "Deal Name Alias", value: deal.deal_name_alias },
            { label: "Currency", value: deal.loan_amount_currency },
            { label: "Margin (%)", value: deal.margin },
            {
              label: "Source",
              value:
                (deal as any).source_entity?.source_name ?? deal.source ?? "-",
            },
            {
              label: "Capital Source",
              value: deal.capital_source?.name ?? "-",
            },
            { label: "Initial Term (y)", value: deal.initial_loan_term },
            { label: "Full Term (y)", value: deal.full_loan_term },
            { label: "Status", value: deal.status?.status },
            {
              label: "Sectors",
              value:
                deal.sectors?.map((sector) => sector.sector).join(", ") || "-",
            },
            {
              label: "Countries",
              value:
                deal.countries?.map((country) => country.nicename).join(", ") ||
                "-",
            },
            { label: "CM Rating", value: deal.cm_rating },
            { label: "WAL", value: deal.wal },
          ]}
        />
        <InfoSection
          title="Sponsor & Borrowers"
          items={[
            {
              label: "Sponsor",
              value: sponsor?.sponsor_name,
              link: sponsor?.id ? `/sponsors/${sponsor.id}` : undefined,
            },
            {
              label: "Borrowers",
              value: formatBorrowers(deal.sponsor_borrowers),
            },
            {
              label: "Secondary Sponsor / Manager",
              value: secondarySponsor?.sponsor_name,
              link: secondarySponsor?.id
                ? `/sponsors/${secondarySponsor.id}`
                : undefined,
            },
            {
              label: "Secondary Borrowers",
              value: formatBorrowers(deal.secondary_sponsor_borrowers),
            },
            { label: "Equity Contribution", value: deal.equity_contribution },
            { label: "Billing Reference", value: deal.deal_billing_reference },
          ]}
        />

        <InfoSection
          title="Flags"
          items={[
            { label: "Expected Closing Date", value: deal.target_closing },
            { label: "IC Date", value: deal.proposed_ic_date },
            { label: "Probability (%)", value: deal.probability },
            { label: "Priority", value: deal.priority },
            { label: "Portfolio", value: deal.is_portfolio ? "Yes" : "No" },
            {
              label: "Development Loan",
              value: deal.is_development_loan ? "Yes" : "No",
            },
            { label: "Funded", value: deal.is_funded ? "Yes" : "No" },
            {
              label: "Term Sheet Completed",
              value: deal.has_complete_term_sheet ? "Yes" : "No",
            },
            {
              label: "Ten Pager Completed",
              value: deal.has_complete_ten_pager ? "Yes" : "No",
            },
          ]}
        />

        <InfoSection
          title="Loan Details"
          items={[
            { label: "Loan Type", value: deal.loan_type?.loan_type },
            {
              label: "Security Type",
              value: deal.security_type?.security_type,
            },
            {
              label: "Transaction Type",
              value: deal.transaction_type?.transaction_type,
            },
            { label: "Risk Class", value: deal.risk_class?.risk_class },
            { label: "Facility Agent Name", value: deal.facility_agent_name },
            { label: "Security Agent Name", value: deal.security_agent_name },
            { label: "Amortisation", value: deal.amortisation ? "Yes" : "No" },
            { label: "Amortisation p.a.", value: deal.amortisation_amount },
            { label: "Amortisation %", value: deal.amortisation_percentage },
            { label: "IRR Local (%)", value: deal.irr_local },
            { label: "IRR USD (%)", value: deal.irr_usd },
            // add irr net of fees
            { label: "IRR Local (Excl. TFC fees)", value: deal.irr_local_net },
            { label: "IRR USD (Excl. TFC fees)", value: deal.irr_usd_net },
          ]}
        />
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%" }}
      >
        <InfoSection
          title="Total Commitments"
          items={[
            { label: "Loan Amount Day 1", value: deal.loan_amount_day_one },
            { label: "Loan Amount FF", value: deal.loan_amount_fully_funded },
            { label: "LTV (%)", value: deal.ltv },
            { label: "LTC (%)", value: deal.ltc },
            { label: "DY (%)", value: deal.dy },
            { label: "ICR (%)", value: deal.icr },
            { label: "DSCR (%)", value: deal.dscr },
          ]}
        />

        <InfoSection
          title="Acquisition Facility"
          items={[
            {
              label: "Loan Amount Day 1",
              value: deal.acq_fac_loan_amount_day_one,
            },
            {
              label: "Loan Amount FF",
              value: deal.acq_fac_loan_amount_fully_funded,
            },
            { label: "LTPP (%)", value: deal.acq_fac_ltpp },
            { label: "DY (%)", value: deal.acq_fac_dy },
            { label: "ICR (%)", value: deal.acq_fac_icr },
            { label: "DSCR (%)", value: deal.acq_fac_dscr },
            {
              label: "Arrangement Fee (%)",
              value: deal.acq_fac_arrangement_fee,
            },
          ]}
        />
        <InfoSection
          title="Development Facility"
          items={[
            {
              label: "Loan Amount Day 1",
              value: deal.dev_fac_loan_amount_day_one,
            },
            {
              label: "Loan Amount FF",
              value: deal.dev_fac_loan_amount_fully_funded,
            },
            { label: "LTC (%)", value: deal.dev_fac_ltc },
            { label: "LTV (%)", value: deal.dev_fac_ltv },
            { label: "LTGDV (%)", value: deal.dev_fac_ltgdv },
            {
              label: "Arrangement Fee (%)",
              value: deal.dev_fac_arrangement_fee,
            },
          ]}
        />
      </Box>

      <Box
        sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%" }}
      >
        <InfoSection
          title="Capex Facility"
          items={[
            {
              label: "Loan Amount Day 1",
              value: deal.capex_fac_loan_amount_day_one,
            },
            {
              label: "Loan Amount FF",
              value: deal.capex_fac_loan_amount_fully_funded,
            },
            { label: "LTC (%)", value: deal.capex_fac_ltc },
            { label: "% of Capex Costs", value: deal.capex_fac_pt_capex_cost },
            {
              label: "Arrangement Fee (%)",
              value: deal.capex_fac_arrangement_fee,
            },
          ]}
        />
        <InfoSection
          title="Interest Reserve Facility"
          items={[
            {
              label: "Loan Amount Day 1",
              value: deal.int_res_fac_loan_amount_day_one,
            },
            {
              label: "% of Capitalised Interest & Commitment Fees",
              value: deal.int_res_fac_pt_cap_int_commitfees,
            },
            {
              label: "Arrangement Fee (%)",
              value: deal.int_res_fac_arrangement_fee,
            },
          ]}
        />
        <InfoSection
          title="VAT Facility"
          items={[
            {
              label: "Loan Amount Day 1",
              value: deal.vat_fac_loan_amount_day_one,
            },
            {
              label: "Loan Amount FF",
              value: deal.vat_fac_loan_amount_fully_funded,
            },
            { label: "% VAT Covered", value: deal.vat_fac_percent_vat_covered },
            {
              label: "Arrangement Fee (%)",
              value: deal.vat_fac_arrangement_fee,
            },
          ]}
        />
      </Box>

      <Box
        sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%" }}
      >
        <InfoSection
          title="Fees"
          items={[
            { label: "Arrangement Fee (%)", value: deal.arrangement_fee },
            {
              label: "Make Whole Fee (months of interest)",
              value: deal.make_whole_fee_interestmonths,
            },
            { label: "Exit Fee (%)", value: deal.exit_fee },
            { label: "Facility Agent Fee", value: deal.facility_agent_fee },
            { label: "Commitment Fee (%)", value: deal.commitment_fee },
            { label: "Extension Fee (%)", value: deal.extension_fee },
            {
              label: "TenFifty Arrangement Fee (%)",
              value: deal.tf_arrangement_fee,
            },
            {
              label: "TenFifty Servicing Fee (%)",
              value: deal.tf_servicing_fee,
            },
          ]}
        />
        <InfoSection
          title="Extension Conditions"
          items={[
            { label: "DSCR (%)", value: deal.extension_condition_dscr },
            { label: "ICR (%)", value: deal.extension_condition_icr },
            { label: "LTV (%)", value: deal.extension_condition_ltv },
            { label: "LTC (%)", value: deal.extension_condition_ltc },
            { label: "DY (%)", value: deal.extension_condition_dy },
            {
              label: "Minimum Occupancy (%)",
              value: deal.extension_condition_occupancy,
            },
          ]}
        />

        <InfoSection
          title="Financial Covenants"
          items={[
            { label: "DSCR (%)", value: deal.covenant_dscr },
            { label: "ICR (%)", value: deal.covenant_icr },
            { label: "LTV (%)", value: deal.covenant_ltv },
            { label: "LTC (%)", value: deal.covenant_ltc },
            { label: "DY (%)", value: deal.covenant_dy },

            {
              label: "DSCR by Year (%)",
              value: unpackYearBasedValue(deal.covenant_dscr_years),
            },
            {
              label: "ICR by Year (%)",
              value: unpackYearBasedValue(deal.covenant_icr_years),
            },
            {
              label: "LTV by Year (%)",
              value: unpackYearBasedValue(deal.covenant_ltv_years),
            },
            {
              label: "LTC by Year (%)",
              value: unpackYearBasedValue(deal.covenant_ltc_years),
            },
            {
              label: "DY by Year (%)",
              value: unpackYearBasedValue(deal.covenant_dy_years),
            },
          ]}
        />
        <InfoSection
          title="Cash Trap Events"
          items={[
            { label: "DSCR (%)", value: deal.cashtrap_dscr },
            { label: "ICR (%)", value: deal.cashtrap_icr },
            { label: "LTV (%)", value: deal.cashtrap_ltv },
            { label: "LTC (%)", value: deal.cashtrap_ltc },
            { label: "DY (%)", value: deal.cashtrap_dy },
            {
              label: "DSCR by Year (%)",
              value: unpackYearBasedValue(deal.cashtrap_dscr_years),
            },
            {
              label: "ICR by Year (%)",
              value: unpackYearBasedValue(deal.cashtrap_icr_years),
            },
            {
              label: "LTV by Year (%)",
              value: unpackYearBasedValue(deal.cashtrap_ltv_years),
            },
            {
              label: "LTC by Year (%)",
              value: unpackYearBasedValue(deal.cashtrap_ltc_years),
            },
            {
              label: "DY by Year (%)",
              value: unpackYearBasedValue(deal.cashtrap_dy_years),
            },
          ]}
        />
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%" }}
      >
        <InfoSection
          title="Deal Purpose"
          items={[{ label: "", value: deal.deal_purpose }]}
        />
        <InfoSection
          title="Deal Summary"
          items={[{ label: "", value: deal.deal_summary }]}
        />
        <InfoSection title="Risks" items={[{ label: "", value: deal.risks }]} />
        <InfoSection
          title="Strategy"
          items={[{ label: "", value: deal.strategy }]}
        />
        <InfoSection
          title="Market Summary"
          items={[{ label: "", value: deal.market_summary }]}
        />
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%" }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: "#ffffff",
            p: 3,
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
            mb: 1,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <Typography
            component="h3"
            sx={{
              m: 0,
              mb: 2.75,
              fontSize: "1rem !important",
              fontWeight: "500 !important",
              color: "#111827",
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            Email Summary
          </Typography>
          <Box
            sx={{
              mt: 0,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <FormattedText text={deal.email_summary} />
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: "#ffffff",
            p: 3,
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
            mb: 1,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <Typography
            component="h3"
            sx={{
              m: 0,
              mb: 2.75,
              fontSize: "1rem !important",
              fontWeight: "500 !important",
              color: "#111827",
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            Exec Summary
          </Typography>
          <Box
            sx={{
              mt: 0,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <FormattedText text={deal.executive_summary} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DealDetail;
